'use client';
// Theme — day/night mode for the whole platform.
// next-themes drives a class on <html> (light default variables in globals.css,
// .dark overrides). The toggle waits for mount to avoid hydration mismatch.
import { useEffect, useState } from 'react';
import { ThemeProvider as NextThemesProvider, useTheme } from 'next-themes';
import { Moon, Sun } from 'lucide-react';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem={false}
      disableTransitionOnChange
      storageKey="aman-theme"
    >
      {children}
    </NextThemesProvider>
  );
}

export function ThemeToggle({ className }: { className?: string }) {
  const { resolvedTheme, setTheme } = useTheme();
  // Microtask deferral keeps SSR markup identical and satisfies the
  // set-state-in-effect rule (same pattern as the session provider).
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    let alive = true;
    Promise.resolve().then(() => { if (alive) setMounted(true); });
    return () => { alive = false; };
  }, []);

  // Placeholder box keeps the header layout stable before hydration
  if (!mounted) {
    return <span className={`inline-flex size-9 rounded-xl ring-1 ring-input ${className ?? ''}`} aria-hidden />;
  }
  const dark = resolvedTheme === 'dark';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={dark}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Day mode' : 'Night mode'}
      onClick={() => setTheme(dark ? 'light' : 'dark')}
      className={`inline-flex size-9 items-center justify-center rounded-xl ring-1 ring-input text-muted-foreground hover:text-primary hover:ring-primary/40 transition ${className ?? ''}`}
    >
      {dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
    </button>
  );
}
